import { t } from "@lingui/macro";
import ordinal from "ordinal";
import React from "react";
import { Image } from "react-native";

import { ReloadIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import type { WhooshThemeColors } from "@whoosh/design-system/src/theme/restyle/theme";
import { getFaceThumbnail } from "@whoosh/utilities/src/Cloudinary";
import {
  CustomColor,
  getLightSurfaceColor,
} from "@whoosh/utilities/src/colors";

import { MultiSessionEventIcon } from "../../../../../../components/icons_v2/events";
import { Box } from "../../../../../../components/primitive";
import { AGENDA_PIXELS_PER_MINUTE } from "../../index";
import { EventStatus, EventStatusType } from "./EventStatus";

type EventCardProps = {
  durationMins: number;
  eventStatus?: EventStatusType;
  eventColor: CustomColor;
  subTitle: string;
  bodyTitle: string;
  times: string;
  onPress?: () => void;
  spotsAvailable?: number;
  publicId?: string;
  opensAt?: string;
  isMultiSession?: boolean;
  isReccuring?: boolean;
  recurrenceIndex?: number;
  numRecurrences?: number;
  eventSessionIndex?: number;
  numSessions?: number;
};

const getEventLabelColor = (color: CustomColor): WhooshThemeColors =>
  `text-user-selectable-color${color}`;

export const EventCard = ({
  durationMins,
  eventStatus,
  eventColor,
  subTitle,
  bodyTitle,
  times,
  onPress,
  spotsAvailable,
  publicId,
  opensAt,
  isMultiSession,
  isReccuring,
  recurrenceIndex,
  numRecurrences,
  eventSessionIndex,
  numSessions,
}: EventCardProps) => {
  const heightInPixels = AGENDA_PIXELS_PER_MINUTE * durationMins;
  const eventTextColor = getEventLabelColor(eventColor);
  return (
    <Pressable
      height={heightInPixels}
      width="100%"
      pb={1}
      flexDirection="column"
      onPress={onPress}
      backgroundColor={getLightSurfaceColor(eventColor)}
      p={4}
      overflow="hidden"
      style={{ borderRadius: 8 }}
    >
      <VStack width="100%" height="100%">
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          color={eventTextColor}
          marginBottom={1}
          lineHeight={16}
          height={20}
          style={{ display: "flex", alignItems: "center" }}
        >
          {t`Event`}
        </Text>
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          color="text-primary"
          numberOfLines={1}
          lineHeight={12}
          height={20}
          style={{ display: "flex", alignItems: "center" }}
        >
          {subTitle}
        </Text>
        {durationMins > 30 ? (
          <>
            <Text
              maxFontSizeMultiplier={2}
              variant="mainTiny"
              color="text-primary"
              marginBottom={1}
              numberOfLines={1}
            >
              {bodyTitle}
            </Text>
            {!!spotsAvailable ? (
              <Text
                maxFontSizeMultiplier={2}
                variant="mainTiny"
                color="text-primary"
                marginBottom={1}
              >
                {spotsAvailable}{" "}
                {spotsAvailable > 1 ? t`Spots Available` : t`Spot Available`}
              </Text>
            ) : null}
            <Text
              maxFontSizeMultiplier={2}
              variant="mainTiny"
              color="text-primary"
              marginBottom={2}
            >
              {times}
            </Text>
            {durationMins > 30 ? (
              <>
                {isReccuring ? (
                  <HStack alignItems="center" gap={1} mb={1}>
                    <ReloadIcon color="grayscale.900" />
                    {recurrenceIndex !== undefined ? (
                      <Text maxFontSizeMultiplier={2} variant="mainTiny">
                        {t`${ordinal(recurrenceIndex)} of ${
                          numRecurrences ?? 0
                        }`}
                      </Text>
                    ) : null}
                  </HStack>
                ) : null}
                {isMultiSession ? (
                  <HStack alignItems="center" mb={1} gap={1}>
                    <MultiSessionEventIcon color="grayscale.900" />
                    {eventSessionIndex !== undefined ? (
                      <Text maxFontSizeMultiplier={2} variant="mainTiny">
                        {t`${ordinal(eventSessionIndex + 1)} of ${
                          numSessions ?? 0
                        }`}
                      </Text>
                    ) : null}
                  </HStack>
                ) : null}
              </>
            ) : null}

            <HStack>
              <EventStatus eventStatus={eventStatus} opensAt={opensAt} />
            </HStack>

            {publicId ? (
              <Box
                width={{ base: "100%", md: "50%" }}
                minHeight={{ base: 104, md: 130 }}
                borderRadius="4"
                overflow="hidden"
                position="relative"
              >
                <Image
                  source={{
                    uri: getFaceThumbnail(publicId)?.toURL(),
                  }}
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                />
              </Box>
            ) : null}
          </>
        ) : null}
      </VStack>
    </Pressable>
  );
};
